"use client";

import React, { useEffect, useState } from "react";
import { Tabs, Tab } from "@heroui/tabs";
import ReportGrid from "@/src/components/companies/ReportGrid";
import { Pagination } from "@heroui/pagination";
import AnalystsNotes from "@/src/components/companies/AnalystsNotes";
import PDFGrid from "@/src/components/companies/PDFGrid";
import BoxReportSkeleton from "@/src/components/companies/BoxReportSkeleton";
import {
  getPrivateCompanyReports,
  getResearchReports,
} from "@/src/app/hooks/content.hooks";
import ResearchReportSkeleton from "./ResearchGridSkeleton";
import ReportGridCompanyProfile from "./ReportGridCompanyProfile";

const ReportSection = ({
  companyId,
  isCompanyPrivate,
  ticker,
  token,
  companyName,
}) => {
  const [researchReports, setResearchReports] = useState([]);
  const [analystNotes, setAnalystNotes] = useState([]);
  const [sort, setSort] = useState("desc");
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState("reports");
  const itemsPerPage = 3;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showResearchReportsTab, setShowResearchReportsTab] = useState(true);

  const handleTabChange = (key) => {
    setSelectedTab(key);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchCompanyReports = async () => {
      setIsLoading(true);
      let response;
      try {
        if (!isCompanyPrivate) {
          response = await getResearchReports(
            currentPage,
            itemsPerPage,
            sort,
            "",
            companyId
          );
        } else {
          response = await getPrivateCompanyReports(
            currentPage,
            itemsPerPage,
            sort,
            token,
            companyId
          );
        }

        if (response.success) {
          setResearchReports(response.data);
          setTotalPages(response.pagination.last_page);
          setShowResearchReportsTab(response.data.length > 0);
        } else {
          setResearchReports([]);
          setTotalPages(0);
          setShowResearchReportsTab(false);
          console.log(response.message);
        }
      } catch (error) {
        console.error("Error fetching reports: ", error);
        setShowResearchReportsTab(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyReports();
  }, [companyId, currentPage, sort, token, isCompanyPrivate]);

  return (
    <section
      id="posts"
      className="p-4 mt-5 bg-white shadow-md rounded-lg border"
    >
      <h2 className="text-xl ml-2 font-bold mb-3">Research Reports</h2>
      <p className=" ml-2 mb-4">
        {companyName || "This company"} has paid FRC a fee for research
        coverage and distribution of reports.
      </p>

      <Tabs
        aria-label="tabs"
        color="primary"
        variant="solid"
        radius="lg"
        classNames={{
          base: "w-full",
          tabList: "bg-gray-50 gap-4 border border-gray-200",
          tab: "px-4 py-2 text-sm font-medium transition-all duration-200 ease-in-out",
          tabContent:
            "group-data-[selected=true]:text-white group-data-[selected=true]:font-bold",
          cursor: "bg-1B51CA ",
        }}
        value={selectedTab}
        onChange={handleTabChange}
      >
        {showResearchReportsTab && (
          <Tab className=" " value="reports" title="Research Reports">
            {selectedTab === "reports" && (
              <>
                {isLoading ? (
                  <ResearchReportSkeleton />
                ) : (
                  <ReportGridCompanyProfile
                    postData={researchReports}
                    companyId={companyId}
                  />
                )}

                {totalPages > 1 && (
                  <Pagination
                    loop
                    showControls
                    color="primary"
                    total={totalPages}
                    initialPage={1}
                    onChange={(page) => setCurrentPage(page)}
                  />
                )}
              </>
            )}
          </Tab>
        )}
        <Tab
          className="text-black font-bold"
          value="old reports"
          title="Past Research Reports"
        >
          <PDFGrid companyId={companyId} isCompanyPrivate={isCompanyPrivate} />
        </Tab>
        <Tab className="font-bold" value="analyst-notes" title="Analyst Notes">
          <AnalystsNotes companyId={companyId || ""} />
        </Tab>
      </Tabs>
    </section>
  );
};

export default ReportSection;
